import { guardArray } from "../array";
import type {
	CheckInRequirements,
	CheckInStepPrompts,
	CheckInStepsResult,
	Nullable,
} from "../types";
import { getHasAllRequiredAcknowledgements } from "./get-has-all-required-acknowledgements";

/**
 * Walks a user through the check-in steps their worksite requires and collects what they confirmed.
 *
 * The definitions come first, the risk assessments second; a step whose list is empty is skipped.
 * Every way out of a step short of a complete confirmation — an abort, a set of ids that leaves a
 * required definition unticked, a declined risk assessment — ends the run as `null`, and the
 * caller checks nobody in.
 *
 * @param requirements - The requirements for the worksite the user is checking in on.
 * @param prompts - The step prompts, one per step, as the client renders them.
 * @returns What the user confirmed, or `null` when a step was not completed.
 */
export async function runCheckInSteps(
	requirements: Nullable<CheckInRequirements>,
	prompts: CheckInStepPrompts,
): Promise<CheckInStepsResult | null> {
	const definitions = guardArray(requirements?.acknowledgementDefinitions);
	const riskAssessments = guardArray(requirements?.riskAssessments);

	let acknowledgedDefinitionIds: string[] = [];
	if (definitions.length > 0) {
		const confirmedIds = await prompts.confirmAcknowledgements();
		if (!Array.isArray(confirmedIds)) return null;
		if (!getHasAllRequiredAcknowledgements(definitions, confirmedIds)) return null;
		acknowledgedDefinitionIds = confirmedIds;
	}

	let acknowledgedRiskAssessments = false;
	if (riskAssessments.length > 0) {
		acknowledgedRiskAssessments = (await prompts.confirmRiskAssessments()) === true;
		if (!acknowledgedRiskAssessments) return null;
	}

	return { acknowledgedDefinitionIds, acknowledgedRiskAssessments };
}
